import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import PlusIcon from "../../../../assets/icons/PlusIcon";
import Spinner from "../../../../assets/icons/Spinner";
import label from "../../../../i18n/label";
import Message from "../../../../i18n/Message";

import ModalDialog from "../../../shared/ModalDialog";
import ToggleSwitch from "../../../shared/toggle/ToggleSwitch";
import "./AddMCPServerDialog.scss";
import backend from "../../../../backend/backend";
import { linkToToolPlugin } from "../../../../routes";

const AddMCPServerDialog = ({ onClose }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState(false);
  const [name, setName] = useState("");
  const [nameInput, setNameInput] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [websiteUrlElem, setWebsiteUrlElem] = useState(null);
  const [mcpServerUrl, setMcpServerUrl] = useState("");
  const [mcpServerUrlElem, setMcpServerUrlElem] = useState(null);

  const [formElem, setFormElem] = useState(null);

  const AUTH_TYPE = {
    NONE: "NONE",
    BEARER_TOKEN: "BEARER_TOKEN",
  };
  const [authRequired, setAuthRequired] = useState(false);
  const [authType, setAuthType] = useState(AUTH_TYPE.BEARER_TOKEN);
  const [bearerToken, setBearerToken] = useState("");
  const [bearerTokenElem, setBearerTokenElem] = useState(null);

  useEffect(() => {
    if (nameInput) nameInput.focus();
  }, [nameInput]);

  const onCreate = () => {
    setValidating(false);
    setValidationError(false);
    const validName = nameInput.checkValidity();
    const validWebsite = websiteUrlElem.checkValidity();
    const validMcpServer = mcpServerUrlElem.checkValidity();
    const validBearer =
      authRequired === AUTH_TYPE.NONE || !bearerTokenElem
        ? true
        : bearerTokenElem.checkValidity();

    if (formElem) formElem.reportValidity();
    if (!validName || !validWebsite || !validMcpServer || !validBearer) return;
    setLoading(true);

    const proceedCreating = () => {
      const req = {
        name,
        websiteUrl,
        mcpServerUrl,
        pluginType: "MCP",
        description: "",
        authType: authRequired ? authType : AUTH_TYPE.NONE,
        authToken:
          authRequired && authType === AUTH_TYPE.BEARER_TOKEN
            ? bearerToken
            : "",
      };
      backend
        .createToolPlugin(workspaceId, req)
        .then((resp) => {
          setAnalyzing(true);

          const checkStatus = async () => {
            try {
              const res = await backend.getToolPlugin(workspaceId, resp.uid);
              if (res.status === "ANALYZED" || res.status === "ERROR") {
                setAnalyzing(false);
                setLoading(false);
                navigate(linkToToolPlugin(workspaceId, resp.uid));
                return;
              }
            } catch (e) {
              console.log("Error analyzing tool plugin status ", e);
            }
            setTimeout(checkStatus, 1500);
          };
          setTimeout(checkStatus, 1500);
        })
        .catch((err) => {
          console.error("Failed to create a tool plugin ", req, err);
          setLoading(false);
        });
    };

    setValidating(true);
    backend
      .validateMcpServer(
        mcpServerUrl,
        authRequired ? authType : AUTH_TYPE.NONE,
        authRequired && authType === AUTH_TYPE.BEARER_TOKEN ? bearerToken : ""
      )
      .then((resp) => {
        setValidating(false);
        if (resp.valid === true) {
          proceedCreating();
        } else {
          setValidationError(resp);
          setLoading(false);
        }
      })
      .catch((err) => {
        setValidating(false);
        setValidationError(true);
        setLoading(false);
        console.error("Failed to validate MCP server", err);
      });
  };
  const onSubmit = (e) => {
    e.preventDefault();
    onCreate();
  };

  return (
    <ModalDialog
      onClose={analyzing ? () => {} : onClose}
      className={
        "create_campaign_dialog add_mcp_server_dialog " +
        (analyzing ? "dark" : "")
      }
    >
      <div className="modal_title">
        <Message id="add_mcp_server_title" />
      </div>
      <form onSubmit={onSubmit} ref={(val) => setFormElem(val)}>
        <div className="campaign_form">
          <div className="dialog_body">
            {!analyzing && (
              <>
                <div className="select_row">
                  <div className="label">
                    <Message id="add_mcp_server_name" />
                  </div>
                  <input
                    className="form-control"
                    type="text"
                    ref={(val) => setNameInput(val)}
                    required
                    maxLength="120"
                    placeholder={label(intl, "add_mcp_server_name_placeholder")}
                    onChange={(e) => setName(e.target.value)}
                    value={name}
                    readOnly={loading}
                  />
                </div>
                <div className="select_row">
                  <div className="label">
                    <Message id="add_mcp_server_mcp_url" />
                  </div>
                  <div className="help">
                    <Message id="add_mcp_server_mcp_url_help" />
                  </div>
                  <div className="help">
                    <Message id="add_mcp_server_mcp_url_suggestion" />
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}{" "}
                    <a
                      role="button"
                      onClick={() => {
                        setMcpServerUrl("https://mcp.context7.com/mcp");
                      }}
                    >
                      <Message id="add_mcp_server_mcp_url_suggestion_link" />
                    </a>
                  </div>
                  <input
                    className="form-control"
                    type="url"
                    ref={(val) => setMcpServerUrlElem(val)}
                    required
                    maxLength="200"
                    placeholder={label(
                      intl,
                      "add_mcp_server_mcp_url_placeholder"
                    )}
                    onChange={(e) => {
                      const value = e.target.value;
                      setMcpServerUrl(value);
                      if (!value) {
                        // Hide auth controls when URL is cleared
                        setAuthRequired(AUTH_TYPE.NONE);
                      }
                    }}
                    value={mcpServerUrl}
                    readOnly={loading}
                  />
                  {(validating || validationError) && (
                    <div className="progress_message">
                      {validating && (
                        <div className="progress_message_info">
                          <Message id="mcp_server_validation_in_progress" />
                        </div>
                      )}
                      {validationError && (
                        <div className="progress_message_error">
                          <Message id="mcp_server_validation_error" />

                          {validationError.message && (
                            <>
                              <div className="validation_error_message_log_intro">
                                <Message id="mcp_server_validation_error_message_intro" />
                                :
                              </div>
                              <div className="validation_error_message_log">
                                {validationError.message}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="select_row">
                  <div className="label">
                    <Message id="add_mcp_server_auth_toggle_label" />
                  </div>
                  <div className="pl-4">
                    <ToggleSwitch
                      inline={true}
                      value={authRequired}
                      onChange={(val) => setAuthRequired(val)}
                      optionLeft={{
                        value: false,
                        label: "add_mcp_server_auth_toggle_left",
                      }}
                      optionRight={{
                        value: true,
                        label: "add_mcp_server_auth_toggle_right",
                      }}
                    />
                  </div>
                </div>

                {authRequired && (
                  <>
                    {/*<div className="select_row">
                      <div className="label">
                        <Message id="add_mcp_server_auth_type_label" />
                      </div>
                      <div>
                        <RadioButtons
                          options={[
                            {
                              value: AUTH_TYPE.BEARER_TOKEN,
                              label: label(
                                intl,
                                "add_mcp_server_auth_bearer"
                              ),
                            },
                          ]}
                          value={authType}
                          onChange={(val) => setAuthType(val)}
                        />
                      </div>
                    </div>*/}
                    <div className="select_row">
                      <div className="label">
                        <Message id="add_mcp_server_auth_token_label" />
                      </div>
                      <input
                        className="form-control"
                        type="text"
                        ref={(val) => setBearerTokenElem(val)}
                        required={
                          authRequired && authType === AUTH_TYPE.BEARER_TOKEN
                        }
                        maxLength="200"
                        placeholder={label(
                          intl,
                          "add_mcp_server_auth_token_placeholder"
                        )}
                        onChange={(e) => setBearerToken(e.target.value)}
                        value={bearerToken}
                        readOnly={loading}
                      />
                    </div>
                  </>
                )}
                <div className="select_row">
                  <div className="label">
                    <Message id="add_mcp_server_website_url" />
                  </div>
                  <div className="help">
                    <Message id="add_mcp_server_website_url_help" />
                  </div>
                  <input
                    className="form-control"
                    type="url"
                    ref={(val) => setWebsiteUrlElem(val)}
                    required
                    maxLength="200"
                    placeholder={label(
                      intl,
                      "add_mcp_server_website_url_placeholder"
                    )}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    value={websiteUrl}
                    readOnly={loading}
                  />
                </div>
              </>
            )}
            {analyzing && (
              <>
                <div className="select_row">
                  <Message id="add_mcp_server_analyzing_text" />
                </div>
                <div>
                  <div className="main-container">
                    <div className="plate"></div>
                    <div className="cup">
                      <div className="top">
                        <div className="vapour">
                          <span style={{ "--i": "1" }}></span>
                          <span style={{ "--i": "2" }}></span>
                          <span style={{ "--i": "3" }}></span>
                          <span style={{ "--i": "19" }}></span>
                          <span style={{ "--i": "20" }}></span>
                          <span style={{ "--i": "4" }}></span>
                          <span style={{ "--i": "5" }}></span>
                          <span style={{ "--i": "6" }}></span>
                          <span style={{ "--i": "17" }}></span>
                          <span style={{ "--i": "18" }}></span>
                          <span style={{ "--i": "7" }}></span>
                          <span style={{ "--i": "8" }}></span>
                          <span style={{ "--i": "9" }}></span>
                          <span style={{ "--i": "15" }}></span>
                          <span style={{ "--i": "16" }}></span>
                          <span style={{ "--i": "10" }}></span>
                          <span style={{ "--i": "11" }}></span>
                          <span style={{ "--i": "12" }}></span>
                          <span style={{ "--i": "13" }}></span>
                          <span style={{ "--i": "14" }}></span>
                        </div>
                        <div className="circle">
                          <div className="coffee"></div>
                        </div>
                      </div>
                      <div className="handle"></div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="buttons">
          <button
            className="btn bg-gradient-dark"
            type="submit"
            disabled={loading}
          >
            {loading && <Spinner size="18" />}
            {!loading && <PlusIcon color="#fff" strokeWidth="2" size="18" />}
            {!analyzing && <Message id="add_mcp_server_button" />}
            {analyzing && <Message id="add_mcp_server_analyzing_button" />}
          </button>
          {!analyzing && (
            <button className="btn no-btn" onClick={onClose}>
              <Message id="cancel" />
            </button>
          )}
        </div>
      </form>
    </ModalDialog>
  );
};

export default AddMCPServerDialog;
